import { GetStreamUserEntity } from "@modules/chat/entities/getStreamUserEntity"
import { CHANNEL_LIST_SHOWN_WINDOW_WIDTH, DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH } from "@modules/chat/utils/chatConstants"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { createContext, useContext, useEffect, useState } from "react"
import { Channel as ChannelType } from "stream-chat"

export type ChatPageContextValue = {
  isCreatingGroup: boolean
  updateIsCreatingGroup: (arg0: boolean) => void
  isCreatingNewChannel: boolean
  updateIsCreatingNewChannel: (arg0: boolean) => void
  isAddingMembersToChannel: ChannelType | undefined
  updateIsAddingMembersToChannel: (arg0: ChannelType | undefined) => void
  displayChannelList: boolean
  toggleDisplayChannelList: () => void
  displayChatDetailsOverlay: boolean
  toggleDisplayChatDetailsOverlay: () => void
  messageToJumpTo: string | null
  setJumpToMessage: (arg0: string | null) => void
  resetMessageToJumpTo: () => void
  selectedUsers: GetStreamUserEntity[]
  toggleUser: (user: GetStreamUserEntity) => void
  removeUser: (index: number) => void
  addUser: (user: GetStreamUserEntity) => void
  clearSelectedUsers: () => void
}

export const ChatPageContext = createContext({} as ChatPageContextValue)

export const ChatPageProvider = (props: React.PropsWithChildren<{ value?: ChatPageContextValue }>) => {
  const [isCreatingNewChannel, updateIsCreatingNewChannel] = useState<boolean>(false)
  const [isAddingMembersToChannel, updateIsAddingMembersToChannel] = useState<ChannelType | undefined>(undefined)
  const [isCreatingGroup, updateIsCreatingGroup] = useState<boolean>(false)
  const [displayChannelList, updateDisplayChannelList] = useState<boolean>(true)
  const [displayChatDetailsOverlay, updateDisplayDetailsOverlay] = useState<boolean>(false)
  const [messageToJumpTo, updateMessageToJumpTo] = useState<string | null>()
  const [selectedUsers, setSelectedUsers] = useState<GetStreamUserEntity[]>([])

  const { children, value } = props
  const { width } = useWindowSize()

  const toggleDisplayChannelList = () => updateDisplayChannelList(!displayChannelList)

  const toggleDisplayChatDetailsOverlay = () => updateDisplayDetailsOverlay(!displayChatDetailsOverlay)

  const setJumpToMessage = (messageId: string): void => {
    updateMessageToJumpTo(messageId)
  }
  const resetMessageToJumpTo = (): void => updateMessageToJumpTo(null)

  const clearSelectedUsers = () => {
    setSelectedUsers([])
  }

  useEffect(() => {
    updateDisplayChannelList(width >= CHANNEL_LIST_SHOWN_WINDOW_WIDTH)
    updateDisplayDetailsOverlay(width >= DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH)
  }, [width])

  useEffect(() => {
    if (displayChannelList && displayChatDetailsOverlay && width < CHANNEL_LIST_SHOWN_WINDOW_WIDTH) {
      toggleDisplayChatDetailsOverlay()
    }
  }, [displayChannelList])

  useEffect(() => {
    if (displayChannelList && displayChatDetailsOverlay && width < CHANNEL_LIST_SHOWN_WINDOW_WIDTH) {
      toggleDisplayChannelList()
    }
  }, [displayChatDetailsOverlay])

  useEffect(() => {
    if (!isCreatingNewChannel && width < CHANNEL_LIST_SHOWN_WINDOW_WIDTH) {
      updateDisplayChannelList(false)
    }
  }, [isCreatingNewChannel])

  useEffect(() => {
    clearSelectedUsers()
  }, [isCreatingGroup])

  const addUser = (user: GetStreamUserEntity) => {
    setSelectedUsers([...selectedUsers, user])
  }

  const removeUser = (index: number) => {
    if (index < 0) {
      return
    }

    setSelectedUsers((prevSelectedUsers) => {
      const newSelectedUsers = prevSelectedUsers.slice()
      newSelectedUsers.splice(index, 1)
      return newSelectedUsers
    })
  }

  const toggleUser = (user: GetStreamUserEntity) => {
    if (!user.id) {
      return
    }

    const existingIndex = selectedUsers.findIndex((u) => u.id === user.id)
    if (existingIndex > -1) {
      removeUser(existingIndex)
    } else {
      addUser(user)
    }
  }

  const chatContext = {
    ...value,
    selectedUsers,
    setSelectedUsers,
    toggleUser,
    removeUser,
    addUser,
    isCreatingGroup,
    updateIsCreatingGroup,
    isCreatingNewChannel,
    updateIsCreatingNewChannel,
    isAddingMembersToChannel,
    updateIsAddingMembersToChannel,
    displayChannelList,
    toggleDisplayChannelList,
    displayChatDetailsOverlay,
    toggleDisplayChatDetailsOverlay,
    messageToJumpTo,
    setJumpToMessage,
    resetMessageToJumpTo,
    clearSelectedUsers,
  }

  return <ChatPageContext.Provider value={chatContext as ChatPageContextValue}>{children}</ChatPageContext.Provider>
}

export const useChatPageContext = () => useContext(ChatPageContext) as unknown as ChatPageContextValue
